/**
 * アウトラインの構造編集を、同期の操作列へ翻訳する。
 *
 * ## なぜ純粋関数なのか
 * 分割・結合・字下げ・字上げの意味論は**画面の都合ではなくアウトライナーの仕様**で、
 * Mac 版（`Packages/BisideCore/Sources/BisideCore/Tree/TreeStore.swift`）に既にある。
 * ここは同じ仕様を TypeScript で書き直したもので、**`cloud/fixtures/outline-edit.json` で
 * 両者を突き合わせる**。画面に埋め込むと、この一致を機械で守れなくなる。
 *
 * ## 新しい操作は要らなかった
 * 4 つとも `insert` / `update` / `move` / `delete` の組み合わせで表現できる。同期の語彙を
 * 増やさずに済むということは、Durable Object 側にも Mac 側にも新しい適用規則が要らない。
 *
 * ## 根はどう表すか
 * クラウドでは最上位のノードの親は `null`。Mac ではワークスペースの根ノードを指す。
 * その読み替えは Mac 側の同期層（P3）が受け持つ。ここでは `null` を根とする。
 */

import { between } from "./fraction-index.ts";
import type { NodeSnapshot, SyncOp } from "./sync-op.ts";

/** 木を読むのに必要な最小の口。`MemoryNodeStore` がそのまま満たす。 */
export interface OutlineTree {
  get(id: string): NodeSnapshot | null;
  /** 生きている子を位置順で返す。 */
  children(parentId: string | null): NodeSnapshot[];
}

/** 編集の結果。`focus` は画面がキャレットを置く場所。 */
export interface EditPlan {
  ops: SyncOp[];
  focus: { nodeId: string; offset: number } | null;
}

const EMPTY: EditPlan = { ops: [], focus: null };

/** 呼び出し側が採番する id の作り手。テストから固定値を差し込めるようにしてある。 */
export type IdFactory = () => string;

const segmenter = new Intl.Segmenter(undefined, { granularity: "grapheme" });

/**
 * 書記素の切れ目に丸める。
 *
 * 画面から来るキャレット位置は UTF-16 単位なので、絵文字や結合文字の**途中**を指し得る。
 * そこで切ると文字が壊れる。Swift 側は書記素単位で数えているため構造上そこを指せない。
 * 丸めることで、両者の分割結果を一致させる。
 */
export function clampToGrapheme(text: string, utf16Offset: number): number {
  const max = text.length;
  const off = Math.max(0, Math.min(utf16Offset, max));
  if (off === 0 || off === max) return off;
  let at = 0;
  for (const seg of segmenter.segment(text)) {
    const end = at + seg.segment.length;
    if (off === at) return at;
    if (off < end) return at; // クラスタの内側 → 手前の境界へ寄せる
    at = end;
  }
  return max;
}

function siblingsOf(tree: OutlineTree, node: NodeSnapshot): NodeSnapshot[] {
  return tree.children(node.parentId);
}

/**
 * 改行。Mac 版と同じ 3 分岐。
 *
 * - A: 末尾で、**見えている子がいる** → 空の行を**最初の子**として差し込む
 * - B: 末尾で、見えている子がいない → 空の行を**次の兄弟**として差し込む
 * - C: 先頭か途中 → **元の行が後半と子を保ち**、前半を持つ新しい行が**手前**に入る
 *
 * C で元の行を残すのがこの仕様の肝で、これにより行に付いた子が付け替わらない。
 * 折りたたんでいる行は「見えている子がいない」として扱う（B に落ちる）。
 */
export function splitPlan(
  tree: OutlineTree,
  nodeId: string,
  caretOffset: number,
  newId: IdFactory,
): EditPlan {
  const node = tree.get(nodeId);
  if (!node || node.deletedAt) return EMPTY;

  const offset = clampToGrapheme(node.text, caretOffset);
  const atEnd = offset === node.text.length;
  const kids = tree.children(nodeId);
  const hasVisibleChildren = kids.length > 0 && !node.collapsed;
  const id = newId();

  // A: 末尾＋見えている子 → 最初の子として空行
  if (atEnd && hasVisibleChildren) {
    const position = between(null, kids[0]?.position ?? null);
    return {
      ops: [{ opId: newId(), kind: "insert", nodeId: id, parentId: nodeId, position, text: "" }],
      focus: { nodeId: id, offset: 0 },
    };
  }

  const siblings = siblingsOf(tree, node);
  const idx = siblings.findIndex((n) => n.id === nodeId);

  // B: 末尾 → 次の兄弟として空行
  if (atEnd) {
    const nextPos = idx >= 0 && idx + 1 < siblings.length ? siblings[idx + 1]!.position : null;
    const position = between(node.position, nextPos);
    return {
      ops: [{ opId: newId(), kind: "insert", nodeId: id, parentId: node.parentId, position, text: "" }],
      focus: { nodeId: id, offset: 0 },
    };
  }

  // C: 先頭か途中 → 前半を手前へ、元の行は後半を持つ
  const prevPos = idx > 0 ? siblings[idx - 1]!.position : null;
  const position = between(prevPos, node.position);
  const prefix = node.text.slice(0, offset);
  const suffix = node.text.slice(offset);
  return {
    ops: [
      { opId: newId(), kind: "insert", nodeId: id, parentId: node.parentId, position, text: prefix },
      { opId: newId(), kind: "update", nodeId, text: suffix },
    ],
    // 元の行に留まる。後半を持っているのはこちらなので、見た目のキャレットは動かない。
    focus: { nodeId, offset: 0 },
  };
}

/**
 * 行頭での削除。Mac 版と同じ扱い。
 *
 * - 最初の兄弟なら**結合ではなく字上げ**（根の直下なら何もしない）
 * - それ以外は**直前の兄弟へ本文を継ぎ足し**、自分の子をその末尾へ移し、自分を消す
 */
export function mergePlan(tree: OutlineTree, nodeId: string, newId: IdFactory): EditPlan {
  const node = tree.get(nodeId);
  if (!node || node.deletedAt) return EMPTY;

  const siblings = siblingsOf(tree, node);
  const idx = siblings.findIndex((n) => n.id === nodeId);
  if (idx < 0) return EMPTY;

  if (idx === 0) {
    // 根の直下では何もしない（消える先が無い）。
    if (node.parentId == null) return EMPTY;
    return outdentPlan(tree, nodeId, newId);
  }

  const prev = siblings[idx - 1]!;
  const ops: SyncOp[] = [
    { opId: newId(), kind: "update", nodeId: prev.id, text: prev.text + node.text },
  ];

  // 自分の子は直前の兄弟の**末尾**へ。順番は保つ。
  const myKids = tree.children(nodeId);
  const prevKids = tree.children(prev.id);
  let lastPos: string | null = prevKids.length ? prevKids[prevKids.length - 1]!.position : null;
  for (const kid of myKids) {
    const position = between(lastPos, null);
    ops.push({ opId: newId(), kind: "move", nodeId: kid.id, parentId: prev.id, position });
    lastPos = position;
  }

  ops.push({ opId: newId(), kind: "delete", nodeId });
  return { ops, focus: { nodeId: prev.id, offset: prev.text.length } };
}

/** 字下げ。**直前の兄弟の最後の子**になる。直前の兄弟が無ければ何もしない。 */
export function indentPlan(tree: OutlineTree, nodeId: string, newId: IdFactory): EditPlan {
  const node = tree.get(nodeId);
  if (!node || node.deletedAt) return EMPTY;

  const siblings = siblingsOf(tree, node);
  const idx = siblings.findIndex((n) => n.id === nodeId);
  if (idx <= 0) return EMPTY;

  const prev = siblings[idx - 1]!;
  const prevKids = tree.children(prev.id);
  const position = between(prevKids.length ? prevKids[prevKids.length - 1]!.position : null, null);
  return {
    ops: [{ opId: newId(), kind: "move", nodeId, parentId: prev.id, position }],
    focus: null,
  };
}

/**
 * 字上げ。1 段上がり、**後続の兄弟を自分の子として引き取る**（Workflowy 互換）。
 *
 * 引き取りが要るのは、そうしないと後続の兄弟が「字上げした行の下」に取り残されて、
 * 見た目の親子関係が入れ替わってしまうから。
 */
export function outdentPlan(tree: OutlineTree, nodeId: string, newId: IdFactory): EditPlan {
  const node = tree.get(nodeId);
  if (!node || node.deletedAt) return EMPTY;
  const parentId = node.parentId;
  if (parentId == null) return EMPTY; // すでに最上位

  const parent = tree.get(parentId);
  if (!parent) return EMPTY;

  const uncles = tree.children(parent.parentId);
  const parentIdx = uncles.findIndex((n) => n.id === parentId);
  const afterParent = parentIdx >= 0 && parentIdx + 1 < uncles.length ? uncles[parentIdx + 1]!.position : null;
  const position = between(parent.position, afterParent);

  const ops: SyncOp[] = [
    { opId: newId(), kind: "move", nodeId, parentId: parent.parentId, position },
  ];

  // 後続の兄弟を自分の子として引き取る。自分の既存の子の後ろへ並べる。
  const siblings = siblingsOf(tree, node);
  const idx = siblings.findIndex((n) => n.id === nodeId);
  const younger = idx >= 0 ? siblings.slice(idx + 1) : [];
  const myKids = tree.children(nodeId);
  let lastPos: string | null = myKids.length ? myKids[myKids.length - 1]!.position : null;
  for (const sib of younger) {
    const childPos = between(lastPos, null);
    ops.push({ opId: newId(), kind: "move", nodeId: sib.id, parentId: nodeId, position: childPos });
    lastPos = childPos;
  }

  return { ops, focus: null };
}
